/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDomainSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling domain form submission and deletion.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { useNginxConfigStore } from '../../../store/nginxConfig';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createDomain, updateDomain, deleteDomain } from '../api/telephonyDomainsApi';
import type { DomainFormState, Domain } from '../types';

function sanitizeDomainForm(state: DomainFormState): DomainFormState {
  return {
    id: state.id,
    domainName: state.domainName.trim(),
    domainDescription: state.domainDescription.trim(),
    tenantId: state.tenantId,
    domainEnabled: Boolean(state.domainEnabled),
    language: state.language.trim() || 'en',
    timezone: state.timezone.trim() || 'UTC',
    countryCode: state.countryCode.trim() || '1',
    certificateId: state.certificateId ?? null,
    serviceType: state.serviceType || 'pbx'
  };
}

export function useDomainSubmit(
  formData: DomainFormState,
  originalData: DomainFormState,
  hasChanges: boolean,
  validateForm: () => { isValid: boolean; errors: Partial<Record<keyof DomainFormState, string>> },
  mode: 'create' | 'edit',
  id: string | undefined,
  setFormData: (data: DomainFormState | ((prev: DomainFormState) => DomainFormState)) => void,
  setOriginalData: (data: DomainFormState) => void
) {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { showError, showSuccess, showInfo } = useNotifications();
  const { markPendingChanges } = useNginxConfigStore();
  const tenantSlug = user?.tenant?.slug ?? 'main';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Submit domain
  const submitDomain = useCallback(async (): Promise<Domain | undefined> => {
    // hasChanges validation removed

    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('telephonyDomains.messages.formValidationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    setIsSubmitting(true);
    try {
      const sanitized = sanitizeDomainForm(formData);

      const response = mode === 'create'
        ? await createDomain(
          {
            domainName: sanitized.domainName,
            domainDescription: sanitized.domainDescription || undefined,
            tenant_id: sanitized.tenantId!,
            domainEnabled: sanitized.domainEnabled,
            language: sanitized.language,
            timezone: sanitized.timezone,
            countryCode: sanitized.countryCode,
            certificateId: sanitized.certificateId,
            serviceType: sanitized.serviceType
          },
          tenantSlug,
          user?.id
        )
        : await updateDomain(
          id!,
          {
            domainName: sanitized.domainName,
            domainDescription: sanitized.domainDescription || undefined,
            tenant_id: sanitized.tenantId,
            domainEnabled: sanitized.domainEnabled,
            language: sanitized.language,
            timezone: sanitized.timezone,
            countryCode: sanitized.countryCode,
            certificateId: sanitized.certificateId,
            serviceType: sanitized.serviceType
          },
          tenantSlug,
          user?.id
        );

      const savedId = response.data?.id ?? formData.id;
      const nextState = sanitizeDomainForm({
        ...sanitized,
        id: typeof savedId === 'number' ? savedId : sanitized.id
      });

      setFormData(nextState);
      setOriginalData(nextState);

      if (mode === 'create') {
        showSuccess(t('telephonyDomains.messages.createSuccess'));
      } else {
        showSuccess(t('telephonyDomains.messages.updateSuccess'));
      }

      // Mark pending SSL changes if domain has a certificate
      if (sanitized.certificateId) {
        markPendingChanges();
      }

      // Return the saved domain data
      return response.data as Domain;
    } catch (error) {
      showError(
        mode === 'create'
          ? t('telephonyDomains.messages.createError')
          : t('telephonyDomains.messages.updateError')
      );
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    hasChanges,
    id,
    markPendingChanges,
    mode,
    setFormData,
    setOriginalData,
    showError,
    showInfo,
    showSuccess,
    t,
    tenantSlug,
    user?.id,
    validateForm
  ]);

  // Handle form submit
  const handleFormSubmit = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>): Promise<Domain | undefined> => {
      event?.preventDefault();
      return await submitDomain();
    },
    [submitDomain]
  );

  // Handle cancel - restore original data
  const handleCancel = useCallback(() => {
    if (mode === 'edit') {
      setFormData(originalData);
    } else {
      setFormData({
        id: undefined,
        domainName: '',
        domainDescription: '',
        tenantId: undefined,
        domainEnabled: true,
        language: 'en',
        timezone: 'UTC',
        countryCode: '1',
        certificateId: null,
        serviceType: 'pbx'
      });
    }
  }, [mode, originalData, setFormData]);

  // Handle delete
  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteDomain(id, tenantSlug, user?.id);
      showSuccess(t('telephonyDomains.messages.deleteSuccess'));
      setShowDeleteModal(false);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('telephonyDomains.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [id, showError, showSuccess, t, tenantSlug, user?.id]);

  return {
    isSubmitting,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    handleFormSubmit,
    handleCancel,
    handleDelete,
    confirmDelete
  };
}

